/**
 * @file Saga updating one charge template in full (concept-contract path); the
 * request body carries the template's three-way `paymentRouteId`.
 *
 * Flow: GLOBAL_CHARGE_TEMPLATE_UPDATE_START → `PUT charge-templates/:id` →
 * unwraps `res.data.data` (returns early when empty) → factory →
 * put(GLOBAL_CHARGE_TEMPLATE_UPDATE_SUCCESS) + snackbar → `onSuccess`.
 * Exceptions: catch → ErrorHandlerSequence(err, action) →
 * put(GLOBAL_CHARGE_TEMPLATE_UPDATE_FAILURE); `onError` via the sequence.
 */
import { call, put, takeEvery } from "redux-saga/effects";
import Constants from "../../Action/Constants";
import { ErrorHandlerSequence } from "../Sequences/ErrorHandlerSequence";
import Http from "../../Http/Http";
import Time from "../../Time/Time";
import ChargeTemplateFactory from "../../Entity/Factories/ChargeTemplateFactory";
import { ActionInterface } from "../../Interface/ActionInterface";
import { ChargeTemplateInterface } from "../../Entity/Entities/Charge/ChargeTemplateInterface";
import Translator from "../../Translation/Translator";

export default function* chargeTemplateUpdate(action: ActionInterface): any {
  try {
    yield put({
      type: Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_CLEAR
    });

    yield put({
      type: Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_REQUEST
    });

    const res = yield call(Http.put, `charge-templates/${action.payload?.chargeTemplate.id}`, {
      amount: action.payload?.chargeTemplate.amount,
      currencyId: action.payload?.chargeTemplate.currency,
      repeatPeriodId: action.payload?.chargeTemplate.repeatPeriod,
      type: action.payload?.chargeTemplate.type,
      description: action.payload?.chargeTemplate.description,
      paymentOffset: action.payload?.chargeTemplate.paymentOffset,
      dueDateFirstPayment: Time.formatOrNull(action.payload?.chargeTemplate.dueDateFirstPayment),
      firstDate: Time.formatOrNull(action.payload?.chargeTemplate.firstDate),
      endDate: Time.formatOrNull(action.payload?.chargeTemplate.endDate),
      isDefault: action.payload?.chargeTemplate.isDefault,
      paymentRouteId: action.payload?.chargeTemplate.paymentRouteId,
      contactId: action.payload?.chargeTemplate.contactId,
      companyId: action.payload?.chargeTemplate.companyId,
      isCollective: action.payload?.chargeTemplate.isCollective
    });

    if (Object.keys(res?.data?.data || {}).length === 0) {
      return;
    }

    const chargeTemplate: ChargeTemplateInterface = ChargeTemplateFactory.create(res.data.data);

    yield put({
      type: Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_SUCCESS,
      payload: {
        chargeTemplate: chargeTemplate
      }
    });

    yield put({
      type: Constants.SNACKBAR_ADD_MESSAGE,
      payload: {
        message: Translator.trans("snackbar-message-charge-template-updated-successfully"),
        type: "success"
      }
    });

    if (action.hasOwnProperty("onSuccess") === true) {
      action.onSuccess.call(this, chargeTemplate);
    }
  } catch (err) {
    yield ErrorHandlerSequence(err, action);

    yield put({
      type: Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_FAILURE,
      payload: {
        err: err
      }
    });
  }
}

export function* watchChargeTemplateUpdate() {
  yield takeEvery(Constants.GLOBAL_CHARGE_TEMPLATE_UPDATE_START, chargeTemplateUpdate);
}
